/**
 * 표의 **가로 스크롤을 손으로 끌 수 있게** 한다 — F620(사장님 지시 2026-08-22)
 *
 * 사장님: *"화살표2 손이나 마우스로 드래그 할 수 있어야 한다.
 *          표 우측에 드래그(가로스크롤된다는) 반투명 화살표 같은 것 넣어줘."*
 *
 * 왜 필요한가 — 표가 넓으면 감싼 상자(`.nxe-tw`)가 가로로 스크롤된다(F646).
 * 그런데 **막대를 정확히 집어야만** 움직였다. 표 위에서 끌면 글자만 선택된다.
 * 그리고 «오른쪽에 더 있다» 는 것을 **막대 말고는 알 길이 없었다.**
 *
 * 여기서 하는 것 셋:
 *   ① 마우스로 표를 **끌면** 가로로 움직인다(막대를 안 집어도 된다).
 *   ② 오른쪽에 **더 있으면** 반투명 화살표가 뜬다(끝까지 가면 사라진다).
 *   ③ 스크롤이 필요 없는 표에는 **아무것도 안 붙인다**(달아 두면 거짓말이다).
 *
 * ★ **손가락(터치)은 건드리지 않는다** — 브라우저가 이미 제대로 한다.
 *   우리가 끼어들면 세로 스크롤까지 뺏는다(그러면 문서를 못 읽는다).
 * ★ **글자 선택을 안 뺏는다** — 가로로 6px 넘게 끌기 전까지는 아무 일도
 *   안 한다. 표 안의 글자를 긁어 복사하는 일이 훨씬 잦다.
 * ★ 왜 파일을 따로 뒀나 — `viewer.ts` 안에 두면 브라우저 하네스가 그 함수를
 *   못 부른다(그 파일은 열자마자 화면을 짓는다). `viewer_code.ts` 와 같은 까닭.
 */

/** 이만큼 가로로 끌어야 «스크롤하려는 것» 으로 본다(그 전엔 글자 선택). */
export const DRAG_SLOP = 6;

/**
 * 가로로 스크롤하는 상자의 선택자.
 *
 * ★★★★★★ F629-2(사장님 지시 2026-08-22): *"코드 박스도 스크롤이 생기면
 *   **표와 같이** 해줘."* — 코드 블록(`pre`)은 스크롤은 됐지만 **막대가 얇고 ·
 *   끌 수 없고 · 오른쪽에 더 있다는 표시가 없었다.** 표에서 이미 만든 것을
 *   **그대로** 쓴다(같은 일을 두 벌로 두지 않는다 — 0-0).
 * ★ `pre` 는 제가 곧 스크롤 상자다(표처럼 감싸는 상자가 따로 없다).
 */
export const WRAP_SEL = '.nxe-tw, .tableWrapper, pre';

/**
 * 화살표가 설 **바깥 상자**의 이름 — F627-1(사장님 지시 2026-08-22).
 *
 * 사장님: *"표 우측 화살표가 표 **상단**에 있어야 하고 가로스크롤시 우측에
 *          **고정**되어 있어야 한다."*
 *
 * ★ 왜 상자를 하나 더 두나 — 화살표를 스크롤 상자(`.nxe-tw`) 안에 두면
 *   **내용과 함께 밀려간다**(그 상자 안에서는 «절대 위치» 도 내용 기준이다).
 *   그래서 끝까지 밀면 화살표가 표를 따라 왼쪽으로 사라졌다.
 *   ⇒ **안 밀리는 상자**를 밖에 하나 두고 거기에 붙인다. 그러면 표가 아무리
 *     움직여도 화살표는 **화면의 그 자리**에 있다.
 * ★ 자식 수·차례는 안 바뀐다 — `.nxe-tw` 가 있던 자리에 이 상자가 들어가고
 *   `.nxe-tw` 는 그 안으로 들어간다(blocks.json 앵커가 조각의 **맨 위 블록
 *   차례**를 쓰므로 그 약속을 깨면 안 된다).
 */
export const OUTER_CLS = 'nxe-twx';

/**
 * 화살표가 타고 내려오는 **세로 레일** — 사장님 지시 2026-08-22:
 * *"표 우측 화살표는 표 우측 **상단**에 위치하고 **세로스크롤되면 표 하단까지
 *   따라 내려오는** 걸로 해줘."*
 *
 * ★ 왜 가상 요소(`::after`)로는 안 되나 — «따라 내려온다» 는 `position:sticky`
 *   다. sticky 는 **흐름 안에 있는 요소**만 된다. `::after` 를 `absolute` 로
 *   붙여 놓으면 상자 안 **한 자리에 못 박히고** 만다(옛 판이 그랬다: 표 상단에
 *   붙어 있다가 문서를 내리면 화면 위로 사라졌다).
 * ★ 그래서 **레일 한 칸**을 표 오른쪽에 세운다(표 높이만큼, 안 밀리는
 *   `.nxe-twx` 기준). 그 안의 화살표가 sticky 다 —
 *     · 표 위쪽이 화면에 있으면  → 표 **상단 오른쪽**
 *     · 문서를 내리면            → 화면에 **붙어 따라 내려온다**
 *     · 표 끝에 닿으면           → 레일 끝(**표 하단**)에서 멈춘다
 *   셋이 한 규칙에서 나온다(따로 재는 자바스크립트가 없다 — 0-0).
 * ★ 클릭을 안 먹는다(`pointer-events:none`) — 표를 끌 때 화살표가 가로막으면
 *   그 자리만 안 끌린다.
 */
export const RAIL_CLS = 'nxe-twa';

/** 오른쪽에 더 있나(1px 여유 — 소수점 반올림 때문에 딱 맞아떨어지지 않는다). */
export function moreRight(el: HTMLElement): boolean {
  return el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
}

/** 스크롤할 것이 있나 · 오른쪽에 더 있나 를 **표식으로** 적는다(CSS 가 읽는다). */
export function mark(el: HTMLElement): void {
  const can = el.scrollWidth > el.clientWidth + 1;
  el.classList.toggle('nxe-tw--scroll', can);
  el.classList.toggle('nxe-tw--more', can && moreRight(el));
}

/**
 * `root` 안의 표 상자에 **끌기 + 화살표**를 단다. 반환 = 손댄 상자 수.
 * 두 번 불러도 안전하다(표식으로 거른다).
 */
export function enableTableScroll(root: ParentNode): number {
  let n = 0;
  root.querySelectorAll<HTMLElement>(WRAP_SEL).forEach((w) => {
    if (w.dataset.nxDrag === '1') { mark(w); return; }   // 두 번 하지 않는다
    w.dataset.nxDrag = '1';
    n += 1;
    // ★ F627-1 — 화살표가 설 **안 밀리는 상자**를 밖에 세운다(위 머리말).
    //   편집기의 `.tableWrapper` 는 ProseMirror 가 쥐고 있으므로 건드리지
    //   않는다 — 남의 것을 감싸면 편집기가 제 상자를 못 찾는다.
    // ★ 편집기가 쥐고 있는 것은 **안 감싼다** — 남의 상자를 감싸면 편집기가
    //   제 상자를 못 찾는다(`.tableWrapper` 도, ProseMirror 안의 `pre` 도).
    const mine = (w.classList.contains('nxe-tw') || w.tagName === 'PRE')
      && !w.closest('.ProseMirror');
    if (mine
        && !(w.parentElement && w.parentElement.classList.contains(OUTER_CLS))) {
      const outer = document.createElement('div');
      outer.className = OUTER_CLS;
      w.replaceWith(outer);
      // ★ 레일이 **먼저** 들어간다 — 표보다 앞에 두면 `.nxe-tw` 는 여전히
      //   상자의 마지막 자식이라 `:has(> .nxe-tw--more)` 규칙이 그대로 산다.
      const rail = document.createElement('div');
      rail.className = RAIL_CLS;
      rail.setAttribute('aria-hidden', 'true');
      rail.appendChild(document.createElement('i'));
      outer.appendChild(rail);
      outer.appendChild(w);
    }
    mark(w);

    // 폭이 바뀌면 다시 잰다 — 창을 줄이면 스크롤이 생기고, 늘리면 사라진다.
    if (typeof ResizeObserver === 'function') {
      new ResizeObserver(() => mark(w)).observe(w);
    }
    w.addEventListener('scroll', () => {
      w.classList.toggle('nxe-tw--more', moreRight(w));
    }, { passive: true });

    let id = -1;          // 누르고 있는 포인터
    let x0 = 0;           // 누른 자리
    let left0 = 0;        // 그때의 스크롤 위치
    let live = false;     // 문턱을 넘어 **끌기가 시작됐나**

    w.addEventListener('pointerdown', (e: PointerEvent) => {
      // ★ 손가락·펜은 브라우저에 맡긴다(위 머리말).
      if (e.pointerType === 'touch' || e.button !== 0) return;
      if (!w.classList.contains('nxe-tw--scroll')) return;
      id = e.pointerId;
      x0 = e.clientX;
      left0 = w.scrollLeft;
      live = false;
    });

    w.addEventListener('pointermove', (e: PointerEvent) => {
      if (e.pointerId !== id) return;
      const dx = e.clientX - x0;
      if (!live) {
        if (Math.abs(dx) < DRAG_SLOP) return;      // 아직 «선택» 일 수 있다
        live = true;
        w.classList.add('nxe-tw--dragging');
        // 여기서부터는 우리가 끈다 — 포인터를 붙잡아 상자 밖으로 나가도
        // 계속 따라오게 한다(표 오른쪽 끝을 넘어가며 끄는 일이 잦다).
        try { w.setPointerCapture(id); } catch { /* 못 잡아도 계속한다 */ }
      }
      w.scrollLeft = left0 - dx;
      e.preventDefault();
    });

    const end = (e: PointerEvent) => {
      if (e.pointerId !== id) return;
      if (live) {
        try { w.releasePointerCapture(id); } catch { /* 이미 놓였다 */ }
      }
      id = -1;
      live = false;
      w.classList.remove('nxe-tw--dragging');
    };
    w.addEventListener('pointerup', end);
    w.addEventListener('pointercancel', end);
    // ★ 끌고 나서 뜨는 클릭은 삼킨다 — 안 그러면 표 안의 링크가 눌린다.
    w.addEventListener('click', (e: MouseEvent) => {
      if (w.dataset.nxDragged === '1') {
        w.dataset.nxDragged = '';
        e.preventDefault();
        e.stopPropagation();
      }
    }, true);
    w.addEventListener('pointerup', () => {
      if (w.classList.contains('nxe-tw--dragging')) w.dataset.nxDragged = '1';
    }, true);
  });
  return n;
}
